// @flow

import { type Axios } from 'axios'
import ClientOAuth2, { Token } from 'client-oauth2'
import ConfigurationShoppingCart from '../FlatConfigurator/MainMenu/Configuration/ShoppingCart/ConfigurationShoppingCart'
import Customer from './Customer'
import { writeToken } from './storage'
import CustomerAuthenticationExpired from './CustomerAuthenticationExpired'
import CustomerAuthenticationMissing from './CustomerAuthenticationMissing'

export type MePayload = {|
  id: string,
  firstName: string,
  lastName?: string,
  email: string,
  phone?: string,
|}

export type ConfigurationReceiptPayload = {|
  configurationId: string,
  optionPrice: number,
  parkingPrice: number,
  lotPriceIncludingTaxes: {|
    amountIncludingTaxes: number,
    vatInPercent: number,
  |},
  hidePrice: boolean,
  totalPrice: number,
  rooms: Array<{|
    label: string,
    price: number,
    extra: boolean,
    categories: Array<{|
      label: string,
      options: Array<{|
        title: string,
        value: string,
        price: number,
        extra: boolean,
      |}>,
    |}>,
  |}>,
  parking: Array<{| label: string, priceIncludingVat: number |}>,
|}

export type ConfigurationPayload = {|
  lotId: string,
  configurationId: string,
  label: string,
  floorLabel: string,
  buildingLabel?: string,
  typology: string,
  area: number,
  lotPriceIncludingTaxes: {|
    amountIncludingTaxes: number,
    vatInPercent: number,
  |},
  hidePrice: boolean,
  optionPrice: number,
  status: string,
  plan: string,
  preview: ?string,
  receipt: ConfigurationReceiptPayload,
  receiptPdfUrl: ?string,
  haveParking: boolean,
  numberOfParking: ?number,
|}

export type ConfigurationsPayload = ConfigurationPayload[]

export type ShoppingCartPayload = {|
  configurationId: string,
  programId: string,
  lotId: string,
  ambianceId: string,
  createdAt: string,
  items: Array<{|
    id: string,
    value: string | boolean | null,
  |}>,
  totalPrice: {|
    amountIncludingTaxes: number,
    vatInPercent: number,
    currency: string,
    amountExcludingTaxes: number,
    vatList: number[],
  |},
  hidePrice: boolean,
|}

type LoginOrRegistrationResult =
  | {|
      token: typeof Token,
      me: MePayload,
    |}
  | {|
      errors: Array<{| field: ?string, message: string |}>,
    |}

const refreshToken = async (token: typeof Token) => {
  if (!token.expired()) {
    return token
  }

  const newToken = await token.refreshToken()
  writeToken(newToken)
}

const refreshCustomerToken = async (customer: Customer): Promise<Customer> => {
  const oldToken = customer.authToken
  const newToken = await refreshToken(oldToken)
  if (newToken === oldToken) {
    return customer
  }

  return customer.refreshToken(newToken)
}

export const loginCustomer = async (
  httpClient: Axios,
  authClient: typeof ClientOAuth2,
  login: string,
  password: string,
): Promise<LoginOrRegistrationResult> => {
  let token
  try {
    token = await authClient.owner.getToken(login, password)
  } catch (err) {
    if (err.code !== 'EAUTH') {
      throw err
    }

    return {
      errors:
        err.body && err.body.error_description
          ? [{ field: null, message: err.body.error_description }]
          : [{ field: null, message: err.toString() }],
    }
  }

  if (!token) {
    throw new Error('No login error detected but no token available neither')
  }

  const meResponse = await fetchMe(httpClient, token)

  return {
    me: meResponse.me,
    token: meResponse.token,
  }
}

export const registerCustomer = async (
  httpClient: Axios,
  authClient: typeof ClientOAuth2,
  programId: string,
  firstName: string,
  email: string,
  password: string,
): Promise<LoginOrRegistrationResult> => {
  try {
    await httpClient.post(`/api/customer`, {
      program: programId,
      firstName,
      email,
      password,
    })
  } catch (err) {
    if (!err.response || err.response.status !== 400) {
      throw err
    }

    return Promise.resolve({
      errors: err.response.data.map(oneError => ({
        field: oneError.source,
        message: oneError.message,
      })),
    })
  }

  return loginCustomer(httpClient, authClient, email, password)
}

export const fetchMe = async (
  httpClient: Axios,
  token: typeof Token,
): Promise<{| me: MePayload, token: typeof Token |}> => {
  token = await refreshToken(token)
  if (!token) {
    throw new CustomerAuthenticationMissing()
  }

  try {
    const response = await httpClient.get(`/api/private/me`, token.sign({}))

    return { token, me: response.data }
  } catch (err) {
    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const saveShoppingCart = async (
  httpClient: Axios,
  customer: Customer,
  shoppingCart: ConfigurationShoppingCart,
): Promise<{| customer: Customer, configurationId: string |}> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.post(
      '/api/private/configuration',
      {
        programId: shoppingCart.programId,
        lotId: shoppingCart.lotId,
        items: shoppingCart.itemsShoppingCart,
        ambianceId: shoppingCart.ambianceId,
      },
      customer.authToken.sign({}),
    )

    return { customer, configurationId: response.data.configurationId }
  } catch (err) {
    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const fetchShoppingCartByLotId = async (
  httpClient: Axios,
  customer: Customer,
  lotId: string,
): Promise<{|
  customer: Customer,
  configurationShoppingCart?: ConfigurationShoppingCart,
|}> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.get(
      `/api/private/configuration?by_lotId=${lotId}`,
      customer.authToken.sign({}),
    )

    const shoppingCartPayload: ShoppingCartPayload = response.data
    if (!shoppingCartPayload) {
      return { customer }
    }

    const configurationShoppingCart = new ConfigurationShoppingCart(
      shoppingCartPayload.configurationId,
      shoppingCartPayload.programId,
      shoppingCartPayload.lotId,
      shoppingCartPayload.totalPrice,
      shoppingCartPayload.hidePrice,
      shoppingCartPayload.items,
      new Date(shoppingCartPayload.createdAt),
      shoppingCartPayload.ambianceId,
    )

    return { customer, configurationShoppingCart }
  } catch (err) {
    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const fetchConfigurations = async (
  httpClient: Axios,
  customer: Customer,
): Promise<{|
  customer: Customer,
  configurationsPayload: ConfigurationsPayload,
|}> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.get(
      `/api/private/configurations`,
      customer.authToken.sign({}),
    )

    return { customer, configurationsPayload: response.data }
  } catch (err) {
    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const fetchConfiguration = async (
  httpClient: Axios,
  customer: Customer,
  configurationId: string,
): Promise<{|
  customer: Customer,
  configurationPayload: ConfigurationPayload,
|}> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.get(
      `/api/private/configuration/${configurationId}`,
      customer.authToken.sign({}),
    )

    return { customer, configurationPayload: response.data }
  } catch (err) {
    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const deleteConfiguration = async (
  httpClient: Axios,
  customer: Customer,
  configurationId: string,
): Promise<{|
  customer: Customer,
|}> => {
  customer = await refreshCustomerToken(customer)

  try {
    await httpClient.delete(
      `/api/private/configurations/${configurationId}`,
      customer.authToken.sign({}),
    )

    return { customer }
  } catch (err) {
    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const configurationCustomerContact = async (
  httpClient: Axios,
  customer: Customer,
  configurationId: string,
  firstName: string,
  lastName: string,
  phone: string,
): Promise<Array<{| field: ?string, message: string |}>> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.post(
      `/api/private/configuration/${configurationId}/contact`,
      {
        firstName: firstName,
        lastName: lastName,
        phone: phone,
      },
      customer.authToken.sign({}),
    )

    return response.data
  } catch (err) {
    if (err.response && err.response.status === 400) {
      return Promise.resolve(
        err.response.data.map(oneError => ({
          field: oneError.source,
          message: oneError.message,
        })),
      )
    }

    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const editProfilCustomer = async (
  httpClient: Axios,
  customer: Customer,
  firstName: string,
  lastName: string,
  email: string,
  phone: string,
): Promise<Array<{| field: ?string, message: string |}>> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.put(
      `/api/private/customer/${customer.id}`,
      {
        firstName: firstName,
        lastName: lastName,
        email: email,
        phone: phone,
      },
      customer.authToken.sign({}),
    )

    return response.data
  } catch (err) {
    if (err.response && err.response.status === 400) {
      return Promise.resolve(
        err.response.data.map(oneError => ({
          field: oneError.source,
          message: oneError.message,
        })),
      )
    }

    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const editPasswordCustomer = async (
  httpClient: Axios,
  customer: Customer,
  oldPassword: string,
  newPassword: string,
): Promise<Array<{| field: ?string, message: string |}>> => {
  customer = await refreshCustomerToken(customer)

  try {
    const response = await httpClient.put(
      `/api/private/customer/password/${customer.id}`,
      {
        oldPassword: oldPassword,
        newPassword: newPassword,
      },
      customer.authToken.sign({}),
    )

    return response.data
  } catch (err) {
    if (err.response && err.response.status === 400) {
      return Promise.resolve(
        err.response.data.map(oneError => ({
          field: oneError.source,
          message: oneError.message,
        })),
      )
    }

    if (err.response && err.response.status === 401) {
      throw new CustomerAuthenticationExpired()
    }

    throw err
  }
}

export const requestNewPassword = async (
  httpClient: Axios,
  programId: string,
  email: string,
): Promise<Array<{| field: ?string, message: string |}>> => {
  try {
    await httpClient.post(`/api/password/request`, {
      program: programId,
      email,
    })
  } catch (err) {
    if (err.response && err.response.status === 400) {
      return err.response.data.map(oneError => ({
        field: oneError.source,
        message: oneError.message,
      }))
    }

    throw err
  }

  return []
}

export const resetPassword = async (
  httpClient: Axios,
  token: string,
  newPassword: string,
): Promise<{
  errors?: {| field: ?string, message: string |}[],
  email: string,
}> => {
  try {
    const response = await httpClient.post(`/api/password/reset`, {
      token,
      newPassword,
    })
    return response.data
  } catch (err) {
    if (err.response && err.response.status === 400) {
      return {
        errors: err.response.data.map(oneError => ({
          field: oneError.source,
          message: oneError.message,
        })),
        email: '',
      }
    }

    if (err.response && err.response.status === 404) {
      return {
        errors: [
          {
            field: 'token',
            message:
              "La demande de token n'existe pas ou est expiré. Veuillez recommencer",
          },
        ],
        email: '',
      }
    }

    throw err
  }
}
